import React from 'react'
import { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { createGlobalStyle } from 'styled-components'
import {
  NextButton,
  PrevButton,
  usePrevNextButtons
} from './EmblaCarouselArrowButtons'

type PropType = {
  slides: Array<{
    src: string
    alt: string
  }>
  options?: EmblaOptionsType
}

const EmblaStyles = createGlobalStyle`
  .embla {
    width: 100%;
    max-width: 100%;
    margin: auto;

    --slide-height: min(34rem, 38vh);
    --slide-spacing: 0.6rem;
    --slide-size: 96%;
    --detail-medium-contrast: rgba(29, 131, 183, 0.18);
    --detail-high-contrast: rgba(29, 131, 183, 0.5);
    --text-body: #16324a;
    --text-high-contrast-rgb-value: 22, 50, 74;
  }

  .embla__viewport {
    overflow: hidden;
  }

  .embla__container {
    display: flex;
    touch-action: pan-y pinch-zoom;
    margin-left: calc(var(--slide-spacing) * -1);
  }

  .embla__slide {
    transform: translate3d(0, 0, 0);
    flex: 0 0 var(--slide-size);
    min-width: 0;
    padding-left: var(--slide-spacing);
  }

  .embla__slide__image {
    border: 0.2rem solid var(--detail-medium-contrast);
    border-radius: 1.8rem;
    height: var(--slide-height);
    overflow: hidden;
    background: linear-gradient(180deg, rgba(29, 131, 183, 0.04), rgba(29, 131, 183, 0.01));
  }

  .embla__slide__image img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #fff;
  }

  .embla__controls {
    display: flex;
    justify-content: center;
    margin-top: 1.8rem;
  }

  @media (max-height: 640px) {
    .embla__controls {
      margin-top: 1rem;
    }
  }

  @media (max-width: 600px) {
    .embla {
      --slide-height: min(26rem, 32vh);
      --slide-size: 100%;
    }

    .embla__slide__image {
      border-radius: 1.2rem;
    }
  }

  .embla__buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
    align-items: center;
    justify-content: center;
  }

  .embla__button {
    -webkit-tap-highlight-color: rgba(var(--text-high-contrast-rgb-value), 0.5);
    -webkit-appearance: none;
    appearance: none;
    background-color: transparent;
    touch-action: manipulation;
    display: inline-flex;
    text-decoration: none;
    cursor: pointer;
    border: 0;
    padding: 0;
    margin: 0;
    border: 0.2rem solid var(--detail-medium-contrast);
    width: 3.6rem;
    height: 3.6rem;

    z-index: 1;
    border-radius: 50%;
    color: var(--text-body);
    display: flex;
    align-items: center;
    justify-content: center;
    transform: rotate(0deg);
  }

  .embla__button--disabled {
    color: var(--detail-high-contrast);
  }

  .embla__button__svg {
    width: 35%;
    height: 35%;
  }

  .embla__dots {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    margin-right: calc((2.6rem - 1.4rem) / 2 * -1);
  }

  .embla__dot {
    -webkit-tap-highlight-color: rgba(var(--text-high-contrast-rgb-value), 0.5);
    -webkit-appearance: none;
    appearance: none;
    background-color: transparent;
    touch-action: manipulation;
    display: inline-flex;
    text-decoration: none;
    cursor: pointer;
    border: 0;
    padding: 0;
    margin: 0;
    width: 2.6rem;
    height: 2.6rem;

    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    position: relative;
  }

  .embla__dot:before,
  .embla__dot:after {
    width: 1.4rem;
    height: 1.4rem;

    border-radius: 50%;
    position: absolute;
    display: flex;
    align-items: center;
    content: '';
  }

  .embla__dot:before {
    border: 0.2rem solid var(--detail-medium-contrast);
  }

  .embla__dot:after {
    border: 0.2rem solid var(--text-body);
    opacity: 0;
  }

  .embla__dot--selected:after {
    opacity: 1;
  }
`

const EmblaCarousel = (props: PropType) => {
  const { slides, options } = props
  const [emblaRef, emblaApi] = useEmblaCarousel(options)

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi)

  return (
    <div className="embla">
      <EmblaStyles />
      <div className="embla__viewport" ref={emblaRef}>
        <div className="embla__container">
          {slides.map((slide, index) => (
            <div className="embla__slide" key={`${slide.src}-${index}`}>
              <div className="embla__slide__image">
                <img src={slide.src} alt={slide.alt} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="embla__controls">
        <div className="embla__buttons">
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>
      </div>
    </div>
  )
}

export default EmblaCarousel
